import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /booking/success?session_id=cs_live_xxx
 *
 * Customer lands here after paying the 50% deposit. We poll the backend status
 * endpoint up to 8 times (2s apart) to confirm the webhook has flipped the
 * order to "deposit_paid". The backend's status route is idempotent — if the
 * webhook hasn't fired yet, polling will trigger the side-effects (emails)
 * itself. Defense in depth so a customer never lands here without their
 * confirmation email being queued.
 */
export default function BookingSuccess() {
    const { lang } = useT();
    const isCn = lang === "cn";
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("loading"); // loading | paid | failed
    const [order, setOrder] = useState(null);

    useEffect(() => {
        let cancelled = false;
        let attempts = 0;
        const poll = async () => {
            if (cancelled) return;
            attempts += 1;
            try {
                const { data } = await axios.get(`${API}/shoot-checkout/status/${sessionId}`, { timeout: 8000 });
                if (data?.payment_status === "paid" && data?.order) {
                    if (cancelled) return;
                    setOrder(data.order);
                    setState("paid");
                    try { window.sessionStorage.removeItem("slowcafe_pending_order"); } catch { /* */ }
                    return;
                }
                if (attempts >= 8) {
                    if (!cancelled) setState("failed");
                    return;
                }
                setTimeout(poll, 2000);
            } catch {
                if (attempts >= 8) {
                    if (!cancelled) setState("failed");
                    return;
                }
                setTimeout(poll, 2500);
            }
        };
        if (sessionId) poll();
        else setState("failed");
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div className="bg-cafe-cream min-h-screen flex flex-col">
            <SiteNav />
            <main className="flex-1 flex items-center justify-center px-6 py-20 md:py-32">
                <div className="max-w-xl w-full text-center">
                    {state === "loading" && (
                        <div data-testid="booking-success-loading">
                            <Loader2 className="h-10 w-10 mx-auto text-cafe-olive animate-spin mb-6" strokeWidth={1.5} />
                            <p className="font-display text-2xl text-cafe-espresso mb-3">
                                {isCn ? "正在确认你的付款…" : "Confirming your payment…"}
                            </p>
                            <p className="text-cafe-mocha text-sm">
                                {isCn ? "请稍等几秒，正在与 Stripe 对账。" : "Hang tight — reconciling with Stripe."}
                            </p>
                        </div>
                    )}

                    {state === "paid" && order && (
                        <div data-testid="booking-success-paid">
                            <div className="h-16 w-16 mx-auto rounded-full bg-cafe-olive/15 flex items-center justify-center mb-6">
                                <Check className="h-8 w-8 text-cafe-olive" strokeWidth={2.2} />
                            </div>
                            <div className="text-[11px] tracking-[0.28em] uppercase text-cafe-olive font-semibold mb-4">
                                {isCn ? "档期已锁定" : "Booking confirmed"}
                            </div>
                            <h1 className="font-display text-4xl md:text-5xl text-cafe-espresso mb-6 leading-[1.1]">
                                {isCn ? "谢谢你！☕" : "Thank you ☕"}
                            </h1>
                            <p className="text-cafe-mocha text-base md:text-lg leading-relaxed mb-8 max-w-md mx-auto">
                                {isCn
                                    ? `已收到 ${order.package_name_cn || order.package_name_en} 的定金 RM ${(order.deposit_cents / 100).toFixed(2)}。Kaily 会在 24 小时内通过 WhatsApp 联系你定档期。`
                                    : `Deposit received for ${order.package_name_en} — RM ${(order.deposit_cents / 100).toFixed(2)}. Kaily will WhatsApp you within 24 hours to lock the date.`}
                            </p>
                            <div className="bg-white border border-cafe-espresso/10 rounded-2xl p-6 text-left text-sm font-mono space-y-1.5 mb-8">
                                <div className="flex justify-between">
                                    <span className="text-cafe-mocha">Order ID</span>
                                    <span className="text-cafe-espresso">{order.id?.slice(0, 8)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-cafe-mocha">{isCn ? "邮箱" : "Email"}</span>
                                    <span className="text-cafe-espresso">{order.customer_email}</span>
                                </div>
                                {order.filming_location && (
                                    <>
                                        <div className="flex justify-between">
                                            <span className="text-cafe-mocha">{isCn ? "拍摄地点" : "Filming location"}</span>
                                            <span className="text-cafe-espresso text-right" data-testid="booking-success-location">{isCn ? order.filming_location_label_cn : order.filming_location_label_en}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-cafe-mocha">{isCn ? "旅程费用" : "Travel"}</span>
                                            <span className="text-cafe-espresso" data-testid="booking-success-travel">
                                                {order.travel_status === "charged" ? `RM ${(order.travel_cents / 100).toFixed(2)}` : order.travel_status === "tbc" ? (isCn ? "待确认" : "To be confirmed") : (isCn ? "已包含" : "Included")}
                                            </span>
                                        </div>
                                    </>
                                )}
                                {order.shoot_date && (
                                    <div className="flex justify-between" data-testid="booking-success-slot">
                                        <span className="text-cafe-mocha">{isCn ? "拍摄档期" : "Shoot date"}</span>
                                        <span className="text-cafe-espresso text-right">
                                            {order.shoot_date} · {order.time_slot === "morning" ? (isCn ? "上午" : "Morning") : (isCn ? "下午" : "Afternoon")}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-cafe-mocha">{isCn ? "已付" : "Paid"}</span>
                                    <span className="text-cafe-olive font-semibold">RM {(order.deposit_cents / 100).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-cafe-mocha">{isCn ? "尾款" : "Balance due"}</span>
                                    <span className="text-cafe-mocha">RM {(order.balance_cents / 100).toFixed(2)}</span>
                                </div>
                            </div>
                            {order.travel_status === "tbc" && (
                                <p className="text-xs text-[#8A6A3E] leading-relaxed mb-4" data-testid="booking-success-travel-tbc">
                                    {isCn ? "旅程费用将由 Kaily 在拍摄预订最终确认前与你确认。" : "Travel charges will be confirmed by Kaily before the production booking is finalised."}
                                </p>
                            )}
                            <p className="text-xs text-cafe-mocha/70 leading-relaxed mb-6">
                                {isCn
                                    ? "📧 一封确认邮件已发到你的邮箱。如果几分钟内没收到，请查看垃圾邮件文件夹。"
                                    : "📧 A confirmation email is on its way. If it doesn't arrive in 5 min, check spam."}
                            </p>
                            <Link
                                to="/"
                                className="inline-flex items-center gap-2 text-cafe-olive hover:text-cafe-oliveDark text-sm font-semibold underline underline-offset-4"
                                data-testid="booking-success-home"
                            >
                                {isCn ? "回首页" : "Back home"} <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                        </div>
                    )}

                    {state === "failed" && (
                        <div data-testid="booking-success-failed">
                            <AlertCircle className="h-10 w-10 mx-auto text-cafe-terracotta mb-6" strokeWidth={1.5} />
                            <p className="font-display text-2xl text-cafe-espresso mb-3">
                                {isCn ? "确认时遇到问题" : "Could not confirm payment"}
                            </p>
                            <p className="text-cafe-mocha text-sm mb-6 leading-relaxed">
                                {isCn
                                    ? "如果你已经在 Stripe 完成付款，请放心 — 付款会通过 webhook 入账。请等几分钟后查收邮件；如果还没收到，请直接 WhatsApp Kaily。"
                                    : "If you completed payment on Stripe, the webhook will reconcile within minutes. Check your email shortly; if not received, WhatsApp Kaily directly."}
                            </p>
                            <a
                                href="https://wa.me/60183628663"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-cafe-olive hover:text-cafe-oliveDark text-sm font-semibold underline underline-offset-4"
                                data-testid="booking-success-whatsapp"
                            >
                                WhatsApp Kaily <ArrowRight className="h-3.5 w-3.5" />
                            </a>
                        </div>
                    )}
                </div>
            </main>
            <SiteFooter />
        </div>
    );
}
